/**
 * Custom error thrown when a gated mutation is blocked by a failed prerequisite.
 *
 * For example, if a site CREATE fails, any observation CREATE mutations
 * in the same scope will throw PrerequisiteFailedError instead of making
 * a network request. This prevents cascading failures.
 *
 * When the prerequisite mutation is retried and succeeds, the scope cascade
 * retry subscriber will automatically re-execute the blocked mutations.
 */
export class PrerequisiteFailedError extends Error {
  name = "PrerequisiteFailedError" as const

  /** The scope ID that this mutation was blocked in */
  scopeId: string

  constructor(scopeId: string) {
    super(`Blocked by failed prerequisite in scope "${scopeId}"`)
    this.scopeId = scopeId
  }
}

/**
 * Type guard for PrerequisiteFailedError.
 * Checks both instanceof and error.name to work after JSON deserialization.
 */
export function isPrerequisiteFailedError(
  error: unknown,
): error is PrerequisiteFailedError {
  if (error instanceof PrerequisiteFailedError) return true
  return (
    error instanceof Error &&
    error.name === "PrerequisiteFailedError" &&
    "scopeId" in error
  )
}
